<template>
  <div class="ev-list-wrap">
    <AError :api="apiDelEvent" />

    <!-- Alert Banner -->
    <AAlertBanner variant="danger" :messages="alertError" title="Warning" />

    <!-- Sub-Header -->
    <div class="ev-sub-header">
      <div class="ev-sub-header__left">
        <h2 class="ev-sub-header__title">{{ model.view.name }}</h2>
        <span class="ev-record-badge" v-if="eventData.length > 0">
          {{ eventData.length }} {{ eventData.length === 1 ? 'record' : 'records' }}
        </span>
      </div>
      <button class="ev-add-btn" @click="toggleEventPage()">
        <v-icon size="16" color="#fff" class="mr-1">mdi-plus</v-icon>
        Add Event
      </button>
    </div>

    <!-- Table Card -->
    <div class="ev-table-card">
      <v-data-table
        :headers="headers"
        :items="eventData"
        :items-per-page="10"
        class="ev-data-table"
        :footer-props="{
          'items-per-page-options': [10, 25, 50],
        }"
      >
        <!-- Grant Application Deadline Column -->
        <template v-slot:item.grantApplicationDeadlineDate="{ item }">
          {{ isValidDate(item.grantApplicationDeadlineDate) ? item.grantApplicationDeadlineDate : "-" }}
        </template>

        <!-- Status Column -->
        <template v-slot:item.status="{ item }">
          <span
            class="ev-status-pill"
            :class="item.status ? 'ev-status-pill--active' : 'ev-status-pill--inactive'"
          >
            <span class="ev-status-pill__dot"></span>
            {{ item.status ? 'Active' : 'Inactive' }}
          </span>
        </template>

        <!-- Actions Column -->
        <template v-slot:item.actions="{ item }">
          <div class="ev-table-actions">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <button
                  class="ev-icon-btn ev-icon-btn--edit"
                  v-bind="attrs"
                  v-on="on"
                  @click="editItem(item)"
                >
                  <v-icon size="15">mdi-pencil</v-icon>
                </button>
              </template>
              <span>Edit</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <button
                  class="ev-icon-btn ev-icon-btn--delete"
                  v-bind="attrs"
                  v-on="on"
                  @click="deleteItem(item)"
                >
                  <v-icon size="15">mdi-delete</v-icon>
                </button>
              </template>
              <span>Delete</span>
            </v-tooltip>
          </div>
        </template>

        <!-- Empty State -->
        <template v-slot:no-data>
          <div class="ev-empty">
            <div class="ev-empty__icon-wrap">
              <v-icon size="40" color="#cbd5e1">mdi-calendar-remove-outline</v-icon>
            </div>
            <span class="ev-empty__title">No events yet</span>
            <span class="ev-empty__text">Create your first event to get started</span>
          </div>
        </template>
      </v-data-table>
    </div>

    <!-- Delete Dialog -->
    <v-dialog v-model="dialogDelete" max-width="380" content-class="confirm-dialog">
      <div class="confirm-card">
        <h3 class="confirm-card__title">Delete {{ model.name.singular }}</h3>
        <p class="confirm-card__message">Are you sure you want to delete this item? This action cannot be undone.</p>
        <div class="confirm-card__actions">
          <button class="confirm-card__btn confirm-card__btn--cancel" @click="closeDelete">
            Cancel
          </button>
          <button class="confirm-card__btn confirm-card__btn--danger" @click="deleteItemConfirm()">
            Delete
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="680" content-class="ev-modal-overlay">
      <div class="ev-modal">
        <div class="ev-modal__header">
          <div class="ev-modal__header-left">
            <v-icon size="18" color="#fff" class="mr-2">mdi-pencil</v-icon>
            <span class="ev-modal__header-title">Edit {{ model.name.singular }}</span>
          </div>
          <button class="ev-modal__close" @click="closeEditDialog()">
            <v-icon size="16" color="rgba(255,255,255,0.8)">mdi-close</v-icon>
          </button>
        </div>
        <div class="ev-modal__body">
          <!-- Campaign ID -->
          <div class="ev-form-group">
            <label class="ev-form-group__label">Campaign ID</label>
            <div class="detail-row" @click="copyContent(editedItem.uuid)">
              <span class="detail-val">{{ editedItem.uuid }}
                <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
              </span>
            </div>
          </div>

          <!-- Title -->
          <div class="ev-form-group">
            <label class="ev-form-group__label">Title</label>
            <v-text-field
              v-model="editedItem.title"
              :error-messages="errorField.title"
              dense
              outlined
              placeholder="Event title"
              hide-details="auto"
              class="ev-form-input"
            ></v-text-field>
          </div>

          <!-- Venue Address -->
          <div class="ev-form-group">
            <label class="ev-form-group__label">Venue Address</label>
            <v-textarea
              dense
              outlined
              auto-grow
              rows="2"
              placeholder="Enter venue address"
              v-model="editedItem.venueAddress"
              :error-messages="errorField.venueAddress"
              hide-details="auto"
              class="ev-form-input"
            ></v-textarea>
          </div>

          <!-- Date Row -->
          <div class="ev-form-row-2col">
            <div class="ev-form-group">
              <label class="ev-form-group__label">Event Start Date</label>
              <v-menu
                ref="menu1"
                v-model="menu1"
                :close-on-content-click="false"
                :return-value.sync="date1"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <div class="rv-datepicker__trigger" v-bind="attrs" v-on="on">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="rv-datepicker__icon">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                    </svg>
                    <span v-if="editedItem.startDate" class="rv-datepicker__text">{{ editedItem.startDate }}</span>
                    <span v-else class="rv-datepicker__placeholder">Select date...</span>
                  </div>
                </template>
                <v-date-picker
                  v-model="editedItem.startDate"
                  no-title
                  scrollable
                  color="var(--app-accent)"
                >
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="menu1 = false">Cancel</v-btn>
                  <v-btn text color="primary" @click="$refs.menu1.save(date1)">OK</v-btn>
                </v-date-picker>
              </v-menu>
            </div>

            <div class="ev-form-group">
              <label class="ev-form-group__label">Event End Date</label>
              <v-menu
                ref="menu2"
                v-model="menu2"
                :close-on-content-click="false"
                :return-value.sync="date2"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <div class="rv-datepicker__trigger" v-bind="attrs" v-on="on">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="rv-datepicker__icon">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                    </svg>
                    <span v-if="editedItem.endDate" class="rv-datepicker__text">{{ editedItem.endDate }}</span>
                    <span v-else class="rv-datepicker__placeholder">Select date...</span>
                  </div>
                </template>
                <v-date-picker
                  v-model="editedItem.endDate"
                  no-title
                  scrollable
                  color="var(--app-accent)"
                >
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="menu2 = false">Cancel</v-btn>
                  <v-btn text color="primary" @click="$refs.menu2.save(date2)">OK</v-btn>
                </v-date-picker>
              </v-menu>
            </div>
          </div>

          <!-- Grant Application Deadline -->
          <div class="ev-form-row-2col">
            <div class="ev-form-group">
              <label class="ev-form-group__label">Event Grant Application Deadline</label>
              <v-menu
                ref="menu3"
                v-model="menu3"
                :close-on-content-click="false"
                :return-value.sync="date3"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <div class="rv-datepicker__trigger" v-bind="attrs" v-on="on">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="rv-datepicker__icon">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                    </svg>
                    <span v-if="editedItem.grantApplicationDeadlineDate" class="rv-datepicker__text">{{ editedItem.grantApplicationDeadlineDate }}</span>
                    <span v-else class="rv-datepicker__placeholder">Select date...</span>
                  </div>
                </template>
                <v-date-picker
                  v-model="editedItem.grantApplicationDeadlineDate"
                  no-title
                  scrollable
                  color="var(--app-accent)"
                >
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="menu3 = false">Cancel</v-btn>
                  <v-btn text color="primary" @click="$refs.menu3.save(date3)">OK</v-btn>
                </v-date-picker>
              </v-menu>
            </div>
          </div>

          <!-- Language -->
          <div class="ev-form-group">
            <label class="ev-form-group__label">Event Language</label>
            <v-select
              dense
              outlined
              :items="languages"
              item-text="isoLanguageName"
              item-value="isoLanguageCode"
              placeholder="Select language"
              v-model="editedItem.language"
              :error-messages="errorField.language"
              :menu-props="{
                bottom: true,
                offsetY: true,
              }"
              hide-details="auto"
              class="ev-form-input"
              style="max-width: 280px"
            ></v-select>
          </div>

          <!-- Category -->
          <div class="ev-form-group">
            <label class="ev-form-group__label">Event Category</label>
            <v-select
              dense
              outlined
              :items="eventCategoriesData"
              placeholder="Select category"
              v-model="editedItem.eventCategory"
              :error-messages="errorField.eventCategory"
              :menu-props="{
                bottom: true,
                offsetY: true,
              }"
              hide-details="auto"
              class="ev-form-input"
            ></v-select>
          </div>

          <!-- Payment Options -->
          <div class="ev-form-group">
            <label class="ev-form-group__label">Payment Made Options</label>
            <div class="ev-toggle-row">
              <div
                class="ev-toggle-card"
                :class="{ 'ev-toggle-card--active': editedItem.isFullPaymentMadeAllow }"
                @click="editedItem.isFullPaymentMadeAllow = !editedItem.isFullPaymentMadeAllow"
              >
                <div class="ev-toggle-card__check">
                  <v-icon v-if="editedItem.isFullPaymentMadeAllow" size="14" color="#fff">mdi-check</v-icon>
                </div>
                <div class="ev-toggle-card__info">
                  <span class="ev-toggle-card__title">Full Payment</span>
                  <span class="ev-toggle-card__desc">Allow full payment option</span>
                </div>
              </div>
              <div
                class="ev-toggle-card"
                :class="{ 'ev-toggle-card--active': editedItem.isNoPaymentMadeAllow }"
                @click="editedItem.isNoPaymentMadeAllow = !editedItem.isNoPaymentMadeAllow"
              >
                <div class="ev-toggle-card__check">
                  <v-icon v-if="editedItem.isNoPaymentMadeAllow" size="14" color="#fff">mdi-check</v-icon>
                </div>
                <div class="ev-toggle-card__info">
                  <span class="ev-toggle-card__title">No Payment</span>
                  <span class="ev-toggle-card__desc">Allow no payment option</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Status -->
          <div class="ev-form-group">
            <label class="ev-form-group__label">Status</label>
            <v-select
              dense
              outlined
              :items="statusOptions"
              placeholder="Select status"
              v-model="editedItem.status"
              :error-messages="errorField.status"
              :menu-props="{
                bottom: true,
                offsetY: true,
              }"
              hide-details="auto"
              class="ev-form-input"
              style="max-width: 280px"
            ></v-select>
          </div>
        </div>

        <div class="ev-modal__footer">
          <button class="ev-modal__btn ev-modal__btn--outlined" @click="closeEditDialog()">
            Cancel
          </button>
          <button class="ev-modal__btn ev-modal__btn--primary" @click="saveEdit()">
            <v-icon size="16" color="#fff" class="mr-1">mdi-content-save-outline</v-icon>
            Save Changes
          </button>
        </div>
      </div>
    </v-dialog>

    <HrdcLoader :loadingDialog="loadingDialog"></HrdcLoader>
  </div>
</template>

<script>
import { mapState } from "vuex";
import Api from "@/objects/api";
import Model from "@/objects/model";
import Service from "@/objects/service";
import hrdcFunction from "@/objects/hrdcFunction";
import HrdcData from "@/services/hrdc_event/objects/globalData";

export default {
  mixins: [hrdcFunction],
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
  },
  props: ["params"],
  emits: ["toggleEventPage"],
  data: () => ({
    apiGetEvents: new Api(),
    apiDelEvent: new Api(),
    apiSaveEdit: new Api(),
    apiGetEventCategories: new Api(),
    model: new Model(),
    service: new Service(),
    hrdcData: new HrdcData(),
    alertError: [],
    date1: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10),
    date2: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10),
    date3: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10),
    menu1: false,
    menu2: false,
    menu3: false,
    loadingDialog: false,
    btnDisabled: false,
    headers: [
      {
        text: "No.",
        align: "start",
        sortable: false,
        value: "number",
      },
      { text: "Title", value: "title" },
      { text: "Event Start Date", value: "startDate" },
      { text: "Event End Date", value: "endDate" },
      {
        text: "Grant Application Deadline",
        value: "grantApplicationDeadlineDate",
      },
      { text: "Status", value: "status" },
      { text: "Actions", value: "actions", sortable: false },
    ],
    editedItem: {
      title: null,
      startDate: null,
      endDate: null,
      grantApplicationDeadlineDate: null,
      language: null,
      status: null,
      eventCategory: null,
      venueAddress: null,
      isFullPaymentMadeAllow: null,
      isNoPaymentMadeAllow: null,
    },
    errorField: {
      title: null,
      startDate: null,
      endDate: null,
      grantApplicationDeadlineDate: null,
      language: null,
      status: null,
      eventCategory: null,
      venueAddress: null,
    },
    eventData: [],
    dialogDelete: false,
    editDialog: false,
    deleteItemData: null,
    statusOptions: [
      {
        text: "Active",
        value: true,
      },
      {
        text: "Inactive",
        value: false,
      },
    ],
    eventCategoriesData: [],
  }),
  async created() {
    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);

    // Events Assignation.
    this.serviceDataAssignation(this.model.serviceKey);

    // Assign apiDetails by mapping repository.
    const apiDetails = {
      apiGetEvents: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/settings/event/get-events`,
      },
      apiDelEvent: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/settings/event/del-event`,
        method: "post",
      },
      apiSaveEdit: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/settings/event/update-event`,
        method: "put",
      },
      apiGetEventCategories: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/settings/event/get-event-categories`,
      },
    };

    for (const api in apiDetails) {
      if (apiDetails[api].url !== undefined) {
        this[api].setUrl(apiDetails[api].url);
      }
      if (apiDetails[api].method !== undefined) {
        this[api].setMethod(apiDetails[api].method);
      }
      if (apiDetails[api].params !== undefined) {
        this[api].setParams(apiDetails[api].params);
      }
    }

    this.apiGetEventCategories.setQuery(`event_type=${this.hrdcType}`);
    this.apiGetEventCategories.setCallbackCompleted((response) => {
      try {
        const { status, data } = response;
        if (status) {
          for (const item of data) {
            this.eventCategoriesData.push({
              text: item.category,
              value: item.uuid,
            });
          }
        }
      } catch (err) {
        console.log(err);
      }
    });

    this.apiGetEvents.setQuery(`event_type=${this.hrdcType}`);
    this.apiGetEvents.setCallbackCompleted((response) => {
      try {
        const { status, data } = response;
        if (status) {
          // Fall back to the start date when the end date is empty/null/invalid,
          // otherwise the table renders "Invalid Date".
          this.eventData = data.map((item) => ({
            ...item,
            endDate: this.isValidDate(item.endDate)
              ? item.endDate
              : item.startDate,
          }));
        }

        this.apiGetEventCategories.fetch();
      } catch (err) {
        console.log(err);
      }
    });
    this.apiGetEvents.fetch();

    this.rolesAssignation(this.auth.roles);
  },
  methods: {
    clearErrorMsg() {
      for (let error in this.errorField) {
        this.errorField[error] = null;
      }
    },
    showErrorMessage(response) {
      for (const key in response.data) {
        this.errorField[key] = null;
        this.errorField[key] = response.data[key];
      }
    },
    isValidDate(value) {
      return Boolean(value) && !isNaN(new Date(value).getTime());
    },
    toggleEventPage() {
      this.$emit("toggleEventPage", "EventAdd");
    },
    editItem(item) {
      this.editedItem = Object.assign({}, item);
      this.editDialog = true;
    },
    deleteItem(item) {
      this.deleteItemData = item;
      this.dialogDelete = true;
    },
    deleteItemConfirm() {
      this.apiDelEvent.setParams({
        id: this.deleteItemData.id,
      });
      this.apiDelEvent.setCallbackCompleted((response) => {
        try {
          const { status, data } = response;
          if (status) {
            this.$store.dispatch("showMessage", {
              message: data,
              messageType: "success",
              timeout: 2000,
            });
            this.apiGetEvents.fetch();
            return true;
          }

          this.$store.dispatch("showMessage", {
            message: "Error, please contact admin to assist",
            messageType: "error",
            timeout: 2000,
          });
        } catch (err) {
          console.log(err);
        }
      });

      this.apiDelEvent.fetch();

      this.closeDelete();
    },
    closeDelete() {
      this.dialogDelete = false;
    },
    getStatusIcon(status) {
      return status ? "mdi-check-circle" : "mdi-close-circle";
    },
    getStatusColor(status) {
      return status ? "green" : "red";
    },
    closeEditDialog() {
      this.editDialog = false;
    },
    saveEdit() {
      this.showLoadingDialog();
      this.editedItem.startDate = new Date(this.editedItem.startDate);
      this.editedItem.endDate = new Date(this.editedItem.endDate);
      this.editedItem.grantApplicationDeadlineDate = this.editedItem
        .grantApplicationDeadlineDate
        ? new Date(this.editedItem.grantApplicationDeadlineDate)
        : null;

      const params = {
        ...this.editedItem,
        userUuid: this.auth.uuid,
      };

      this.apiSaveEdit.setParams(params);
      this.apiSaveEdit.setCallbackCompleted((response) => {
        try {
          this.clearErrorMsg();

          if (!response.status) {
            this.showErrorMessage(response);
            this.$store.dispatch("showMessage", {
              message: "Please fill in all the required fields",
              messageType: "error",
              timeout: 2000,
            });

            return false;
          }

          if (response.status) {
            this.apiGetEvents.fetch();

            this.closeEditDialog();
            this.$store.dispatch("showMessage", {
              message: response.data,
              messageType: "success",
              timeout: 2000,
            });
          }
        } catch (err) {
          console.log(err);
        } finally {
          this.hideLoadingDialog();
        }
      });
      this.apiSaveEdit.fetch();
    },
    copyContent(item) {
      navigator.clipboard.writeText(item).then(() => {
        this.$store.dispatch("showMessage", {
          message: "Copied Successfully!",
          messageType: "info",
        });
      });
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .ev-sub-header__title {
  color: var(--app-text);
}

.dark-mode .ev-record-badge {
  background: var(--app-elevated);
  color: var(--app-text-muted);
}

.dark-mode .ev-table-card {
  background: var(--app-card);
  border: 1px solid var(--app-border);
}

/* Dark pills keep the light-mode footprint (inset ring, no border) but swap to
   the light-on-dark foreground tokens — the light-mode #dc2626 / #059669 text
   is unreadable on a dark tinted background. */
.dark-mode .ev-status-pill--active {
  background: var(--app-success-bg);
  color: var(--app-success-fg);
  box-shadow: inset 0 0 0 1px var(--app-success-border);
}

.dark-mode .ev-status-pill--active .ev-status-pill__dot {
  background: var(--app-success);
}

.dark-mode .ev-status-pill--inactive {
  background: var(--app-danger-bg);
  color: var(--app-danger-fg);
  box-shadow: inset 0 0 0 1px var(--app-danger-border);
}

.dark-mode .ev-status-pill--inactive .ev-status-pill__dot {
  background: var(--app-danger);
}

.dark-mode .ev-icon-btn--edit:hover {
  background: var(--app-accent-soft);
}

.dark-mode .ev-icon-btn--delete:hover {
  background: var(--app-danger-bg);
}

.dark-mode .ev-empty__icon-wrap {
  background: var(--app-surface);
}

.dark-mode .ev-empty__title {
  color: var(--app-neutral-fg);
}

.dark-mode .ev-empty__text {
  color: var(--app-text-faint);
}

.dark-mode .ev-modal {
  background: var(--app-card);
}

.dark-mode .ev-modal__body::-webkit-scrollbar-thumb {
  background: var(--app-elevated);
}

.dark-mode .ev-modal__warning-icon {
  background: var(--app-danger-bg);
}

.dark-mode .ev-modal__heading {
  color: var(--app-text);
}

.dark-mode .ev-modal__message {
  color: var(--app-text-muted);
}

.dark-mode .ev-modal__footer {
  border-top: 1px solid var(--app-border);
}

.dark-mode .ev-modal__btn--outlined {
  background: var(--app-card);
  color: var(--app-text-muted);
  border: 1.5px solid var(--app-border);
}

.dark-mode .ev-modal__btn--outlined:hover {
  background: var(--app-surface);
  border-color: var(--app-border-strong);
}

.dark-mode .detail-val {
  border: 1px solid var(--app-border);
}

.dark-mode .rv-datepicker__trigger {
  border: 1px solid var(--app-border);
  background: var(--app-card);
}

.dark-mode .rv-datepicker__trigger:hover {
  border-color: var(--app-border-strong);
}

.dark-mode .rv-datepicker__icon {
  color: var(--app-text-faint);
}

.dark-mode .rv-datepicker__text {
  color: var(--app-text);
}

.dark-mode .rv-datepicker__placeholder {
  color: var(--app-text-faint);
}

.dark-mode .ev-form-group__label {
  color: var(--app-text-muted);
}

.dark-mode .ev-toggle-card {
  border: 1.5px solid var(--app-border);
  background: var(--app-card);
}

.dark-mode .ev-toggle-card:hover {
  border-color: var(--app-border-strong);
  background: var(--app-surface);
}

.dark-mode .ev-toggle-card--active {
  background: var(--app-accent-soft);
}

.dark-mode .ev-toggle-card--active:hover {
  background: var(--app-accent-soft);
}

.dark-mode .ev-toggle-card__check {
  border: 2px solid var(--app-border-strong);
  background: var(--app-card);
}

.dark-mode .ev-toggle-card__title {
  color: var(--app-text);
}

.dark-mode .ev-toggle-card__desc {
  color: var(--app-text-faint);
}

/* >>> end auto-dark <<< */

/* ============================================
   LIST WRAPPER (inside tab content)
   ============================================ */
.ev-list-wrap {
  padding: 24px;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ============================================
   SUB-HEADER
   ============================================ */
.ev-sub-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.ev-sub-header__left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ev-sub-header__title {
  font-size: 1.05rem;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  letter-spacing: -0.2px;
}

.ev-record-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 12px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 600;
}

.ev-add-btn {
  display: inline-flex;
  align-items: center;
  padding: 9px 18px;
  border-radius: 10px;
  background: #0ea5e9;
  color: #fff;
  font-family: 'Lato', sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(14, 165, 233, 0.2);
}

.ev-add-btn:hover {
  background: #0284c7;
  box-shadow: 0 4px 16px rgba(14, 165, 233, 0.3);
  transform: translateY(-1px);
}

/* ============================================
   TABLE CARD
   ============================================ */
.ev-table-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

/* ============================================
   STATUS PILL
   ============================================ */
.ev-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.ev-status-pill__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.ev-status-pill--active {
  background: #ecfdf5;
  color: #059669;
}

.ev-status-pill--active .ev-status-pill__dot {
  background: #10b981;
}

.ev-status-pill--inactive {
  background: #fef2f2;
  color: #dc2626;
}

.ev-status-pill--inactive .ev-status-pill__dot {
  background: #ef4444;
}

/* ============================================
   TABLE ACTION BUTTONS
   ============================================ */
.ev-table-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ev-icon-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.ev-icon-btn--edit {
  color: #0ea5e9;
}

.ev-icon-btn--edit:hover {
  background: #f0f9ff;
  border-color: #bae6fd;
}

.ev-icon-btn--edit .v-icon {
  color: #0ea5e9 !important;
}

.ev-icon-btn--delete {
  color: #dc2626;
}

.ev-icon-btn--delete:hover {
  background: #fef2f2;
  border-color: #fecaca;
}

.ev-icon-btn--delete .v-icon {
  color: #dc2626 !important;
}

/* ============================================
   EMPTY STATE
   ============================================ */
.ev-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 0;
}

.ev-empty__icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
}

.ev-empty__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #475569;
}

.ev-empty__text {
  font-size: 0.82rem;
  color: #94a3b8;
  font-weight: 500;
}

/* ============================================
   MODAL / DIALOG
   ============================================ */
.ev-modal {
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.ev-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
}

.ev-modal__header-left {
  display: flex;
  align-items: center;
}

.ev-modal__header-title {
  font-family: 'Lato', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  color: #fff;
}

.ev-modal__close {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.ev-modal__close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.ev-modal__body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.ev-modal__body::-webkit-scrollbar {
  width: 5px;
}

.ev-modal__body::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}

.ev-modal__footer {
  flex-shrink: 0;
}

.ev-modal__body--center {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px 24px 24px;
}

.ev-modal__warning-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #fef2f2;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.ev-modal__heading {
  font-family: 'Lato', sans-serif;
  font-size: 1.05rem;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 8px;
}

.ev-modal__message {
  font-family: 'Lato', sans-serif;
  font-size: 0.88rem;
  font-weight: 500;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}

.ev-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid #f1f5f9;
}

.ev-modal__btn {
  display: inline-flex;
  align-items: center;
  padding: 9px 20px;
  border-radius: 10px;
  font-size: 0.84rem;
  font-weight: 700;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
  border: none;
}

.ev-modal__btn--outlined {
  background: #fff;
  color: #64748b;
  border: 1.5px solid #e2e8f0;
}

.ev-modal__btn--outlined:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.ev-modal__btn--primary {
  background: #0ea5e9;
  color: #fff;
  box-shadow: 0 2px 8px rgba(14, 165, 233, 0.2);
}

.ev-modal__btn--primary:hover {
  background: #0284c7;
  box-shadow: 0 4px 16px rgba(14, 165, 233, 0.3);
}

.ev-modal__btn--danger {
  background: #dc2626;
  color: #fff;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.2);
}

.ev-modal__btn--danger:hover {
  background: #b91c1c;
  box-shadow: 0 4px 16px rgba(220, 38, 38, 0.3);
}

/* ============================================
   FORM GROUPS (inside modals)
   ============================================ */
/* Detail row (click-to-copy) */
.detail-row {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.detail-val {
  flex: 1;
  font-size: 0.85rem;
  color: #1a1e24;
  line-height: 1.5;
  word-break: break-all;
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  background: #f1f3f5;
  border: 1px solid #e2e4e8;
  border-radius: 8px;
  padding: 7px 14px;
  transition: all 0.15s ease;
}

.detail-val:hover {
  background: #eef1f5;
  border-color: #c9cdd3;
}

.copy-icon {
  opacity: 0;
  transition: opacity 0.2s ease;
  margin-left: 8px;
  color: #a0aab4 !important;
}

.detail-row:hover .copy-icon {
  opacity: 1;
}

/* Datepicker trigger */
.rv-datepicker__trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 42px;
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: 'Lato', sans-serif;
  font-size: 0.85rem;
}

.rv-datepicker__trigger:hover {
  border-color: #94a3b8;
}

.rv-datepicker__icon {
  width: 18px;
  height: 18px;
  color: #94a3b8;
  flex-shrink: 0;
}

.rv-datepicker__text {
  color: #1e293b;
  font-weight: 600;
}

.rv-datepicker__placeholder {
  color: #94a3b8;
}

.ev-form-group {
  margin-bottom: 16px;
}

.ev-form-group__label {
  display: block;
  font-family: 'Lato', sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.ev-form-row-2col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

/* Toggle Cards */
.ev-toggle-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.ev-toggle-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ev-toggle-card:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.ev-toggle-card--active {
  border-color: #0ea5e9;
  background: #f0f9ff;
}

.ev-toggle-card--active:hover {
  border-color: #0284c7;
  background: #e0f2fe;
}

.ev-toggle-card__check {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s ease;
  background: #fff;
}

.ev-toggle-card--active .ev-toggle-card__check {
  background: #0ea5e9;
  border-color: #0ea5e9;
}

.ev-toggle-card__info {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.ev-toggle-card__title {
  font-family: 'Lato', sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  color: #1e293b;
}

.ev-toggle-card--active .ev-toggle-card__title {
  color: #0369a1;
}

.ev-toggle-card__desc {
  font-family: 'Lato', sans-serif;
  font-size: 0.7rem;
  color: #94a3b8;
}

@media (max-width: 599px) {
  .ev-toggle-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 599px) {
  .ev-form-row-2col {
    grid-template-columns: 1fr;
  }
}

/* ============================================
   TRANSITIONS
   ============================================ */
.slide-fade-enter-active { transition: all 0.3s ease; }
.slide-fade-leave-active { transition: all 0.2s ease; }
.slide-fade-enter { transform: translateY(-8px); opacity: 0; }
.slide-fade-leave-to { transform: translateY(-4px); opacity: 0; }

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 768px) {
  .ev-list-wrap { padding: 16px; }
  .ev-sub-header { flex-direction: column; align-items: flex-start; gap: 12px; }
  .ev-form-row-2col { grid-template-columns: 1fr; }
}

/* ============================================
   DARK MODE
   ============================================ */
.dark-mode .ev-list-wrap { color: var(--app-text); }
.dark-mode .ev-sub-header__title { color: var(--app-text); }
.dark-mode .ev-record-badge { background: var(--app-border); color: var(--app-text-muted); }
.dark-mode .ev-table-card { background: var(--app-card); border-color: var(--app-border); }
.dark-mode .ev-modal { background: var(--app-card); }
.dark-mode .ev-modal__heading { color: var(--app-text); }
.dark-mode .ev-modal__message { color: var(--app-text-muted); }
.dark-mode .ev-modal__footer { border-color: var(--app-border); }
.dark-mode .ev-form-group__label { color: var(--app-neutral-fg); }
</style>

<style>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .ev-data-table .v-data-table-header {
  background: var(--app-surface);
}

.dark-mode .ev-data-table .v-data-table-header th {
  color: var(--app-text-muted) !important;
  border-bottom: 1px solid var(--app-border) !important;
}

.dark-mode .ev-data-table tbody tr:hover {
  background: var(--app-surface) !important;
}

.dark-mode .ev-data-table tbody td {
  border-bottom: 1px solid var(--app-border) !important;
  color: var(--app-text);
}

.dark-mode .ev-data-table .v-data-footer {
  border-top: 1px solid var(--app-border) !important;
  background: var(--app-surface);
}

.dark-mode .ev-data-table .v-data-footer__select {
  color: var(--app-text-muted) !important;
}

.dark-mode .ev-data-table .v-data-footer__select .v-input__slot {
  background: var(--app-card) !important;
  border: 1px solid var(--app-border) !important;
}

.dark-mode .ev-data-table .v-data-footer__pagination {
  color: var(--app-text-muted) !important;
}

.dark-mode .ev-data-table .v-data-footer__icons-before .v-btn,
.dark-mode .ev-data-table .v-data-footer__icons-after .v-btn {
  background: var(--app-card) !important;
  border: 1px solid var(--app-border) !important;
}

.dark-mode .ev-data-table .v-data-footer__icons-before .v-btn:hover:not(.v-btn--disabled),
.dark-mode .ev-data-table .v-data-footer__icons-after .v-btn:hover:not(.v-btn--disabled) {
  background: var(--app-accent-soft) !important;
}

.dark-mode .ev-data-table .v-data-footer__icons-before .v-btn .v-icon,
.dark-mode .ev-data-table .v-data-footer__icons-after .v-btn .v-icon {
  color: var(--app-text-muted) !important;
}

.dark-mode .ev-form-input.v-text-field--outlined fieldset {
  border-color: var(--app-border) !important;
}

.dark-mode .ev-form-input.v-text-field--outlined:hover fieldset {
  border-color: var(--app-border-strong) !important;
}

.dark-mode .ev-form-input input,
.dark-mode .ev-form-input textarea,
.dark-mode .ev-form-input .v-select__selection {
  color: var(--app-text) !important;
}

.dark-mode .ev-form-input input::placeholder,
.dark-mode .ev-form-input textarea::placeholder {
  color: var(--app-text-faint) !important;
}

.dark-mode .ev-form-input .v-icon {
  color: var(--app-text-faint) !important;
}

.dark-mode .ev-form-input.v-input--is-disabled .v-input__slot {
  background: var(--app-surface) !important;
}

.dark-mode .ev-form-input.v-input--is-disabled.v-text-field--outlined fieldset {
  background: var(--app-surface);
}

.dark-mode .ev-form-input.v-input--is-disabled input {
  color: var(--app-text-faint) !important;
}

.dark-mode .v-picker__body {
  background: var(--app-card) !important;
}

.dark-mode .v-date-picker-header {
  background: var(--app-card) !important;
}

.dark-mode .v-date-picker-header__value button {
  color: var(--app-text) !important;
}

.dark-mode .v-date-picker-header .v-btn {
  color: var(--app-text-muted) !important;
}

.dark-mode .v-date-picker-table thead th {
  color: var(--app-text-faint) !important;
}

.dark-mode .v-date-picker-table .v-btn {
  color: var(--app-text) !important;
}

.dark-mode .v-date-picker-table .v-btn:hover {
  background: var(--app-accent-soft) !important;
}

.dark-mode .v-picker__actions {
  background: var(--app-card) !important;
  border-top: 1px solid var(--app-border) !important;
}

/* >>> end auto-dark <<< */

/* ============================================
   DATA TABLE OVERRIDES (unscoped)
   ============================================ */
.ev-data-table {
  background: transparent !important;
  box-shadow: none !important;
}

.ev-data-table .v-data-table-header {
  background: #f8fafc;
}

.ev-data-table .v-data-table-header th {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.7rem !important;
  font-weight: 700 !important;
  color: #64748b !important;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  border-bottom: 1px solid #e2e8f0 !important;
  padding: 12px 16px !important;
  white-space: nowrap;
}

.ev-data-table tbody tr {
  transition: background 0.15s ease;
}

.ev-data-table tbody tr:hover {
  background: #f8fafc !important;
}

.ev-data-table tbody td {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.84rem !important;
  font-weight: 600 !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
  color: #1e293b;
}

.ev-data-table .v-data-footer {
  border-top: 1px solid #e2e8f0 !important;
  font-family: 'Lato', sans-serif !important;
  padding: 10px 16px !important;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.ev-data-table .v-data-footer__select {
  font-size: 0.76rem !important;
  color: #64748b !important;
  font-weight: 600;
  margin-right: 16px !important;
}

.ev-data-table .v-data-footer__select .v-select {
  font-family: 'Lato', sans-serif !important;
  margin: 0 0 0 8px !important;
}

.ev-data-table .v-data-footer__select .v-input__slot {
  background: #fff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  min-height: 30px !important;
  padding: 0 8px !important;
  box-shadow: none !important;
}

.ev-data-table .v-data-footer__select .v-input__slot::before,
.ev-data-table .v-data-footer__select .v-input__slot::after {
  display: none !important;
}

.ev-data-table .v-data-footer__pagination {
  font-size: 0.76rem !important;
  color: #64748b !important;
  font-weight: 600;
  margin: 0 8px !important;
}

.ev-data-table .v-data-footer__icons-before,
.ev-data-table .v-data-footer__icons-after {
  display: inline-flex;
  gap: 4px;
}

.ev-data-table .v-data-footer__icons-before .v-btn,
.ev-data-table .v-data-footer__icons-after .v-btn {
  width: 30px !important;
  height: 30px !important;
  background: #fff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  margin: 0 2px !important;
  transition: all 0.15s ease;
}

.ev-data-table .v-data-footer__icons-before .v-btn:hover:not(.v-btn--disabled),
.ev-data-table .v-data-footer__icons-after .v-btn:hover:not(.v-btn--disabled) {
  background: #e0f2fe !important;
  border-color: #0ea5e9 !important;
}

.ev-data-table .v-data-footer__icons-before .v-btn--disabled,
.ev-data-table .v-data-footer__icons-after .v-btn--disabled {
  opacity: 0.35 !important;
}

.ev-data-table .v-data-footer__icons-before .v-btn .v-icon,
.ev-data-table .v-data-footer__icons-after .v-btn .v-icon {
  color: #64748b !important;
  font-size: 18px !important;
}

/* Form input overrides for outlined style */
/* Vuetify offsets the outlined fieldset to top: -5px so its <legend> can notch
   out the floating label. These fields use a real <label> above the box, so the
   overhang is just an empty band above the content — and any background on
   .v-input__slot stops 5px short of the border, which reads as a gap. Align the
   fieldset with the slot and the band is gone. */
.ev-form-input.v-text-field--outlined fieldset {
  border-color: #e2e8f0 !important;
  border-radius: 10px !important;
  top: 0 !important;
}

.ev-form-input.v-text-field--outlined:hover fieldset {
  border-color: #cbd5e1 !important;
}

.ev-form-input.v-input--is-focused fieldset {
  border-color: #0ea5e9 !important;
  border-width: 2px !important;
}

.ev-form-input .v-input__slot {
  min-height: 42px !important;
}

/* With the fieldset pulled down to top: 0 the border no longer reaches up into
   the label gap, so drop the 4px Vuetify reserves above every field for its
   floating label — .ev-form-group__label owns that spacing. */
.ev-form-input.v-text-field {
  margin-top: 0 !important;
}

.ev-form-input input,
.ev-form-input textarea,
.ev-form-input .v-select__selection {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.85rem !important;
  font-weight: 600 !important;
  color: #1e293b !important;
}

.ev-form-input input::placeholder,
.ev-form-input textarea::placeholder {
  color: #94a3b8 !important;
  font-weight: 500 !important;
}

.ev-form-input .v-icon {
  color: #94a3b8 !important;
}

.ev-form-input.v-input--is-disabled .v-input__slot {
  background: #f8fafc !important;
}

/* The outlined <fieldset> that carries the border sits 5px above
   .v-input__slot (Vuetify's top: -5px for the label notch), so a fill on the
   slot leaves an unpainted band under the top border. Fill the fieldset —
   which owns the border box — and lift the content above it, since the
   fieldset is absolutely positioned and would otherwise paint over the text. */
.ev-form-input.v-input--is-disabled.v-text-field--outlined fieldset {
  background: #f8fafc;
}

.ev-form-input .v-input__slot > *:not(fieldset) {
  position: relative;
  z-index: 1;
}

.ev-form-input.v-input--is-disabled input {
  color: #94a3b8 !important;
}

/* Date picker popup */
.v-picker {
  border-radius: 14px !important;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
  border: none !important;
  overflow: hidden;
  font-family: 'Lato', sans-serif !important;
}

.v-picker .v-picker__body {
  border: none !important;
}

/* The unscoped `.v-menu__content …` block that used to sit here is gone.
   It was global — unscoped CSS is injected once and never removed, so a rule
   written for THIS page's dropdown repainted every menu in the app (with
   !important, and with hard-coded light hex) for the rest of the session. That
   is what made the same v-select look different depending on which page you had
   visited. Menus are defined ONCE, in tokens, in src/plugins/theme-glass.css §3
   — panel, list, item radius, hover, active, typography and the state overlay.
   A page that genuinely needs its own menu styling should give that v-menu a
   `content-class` and key off it, the way .bdt-menu and .tb-view-menu do. */

.v-picker__body {
  background: #fff !important;
}

.v-date-picker-header {
  padding: 12px 16px 8px !important;
  background: #fff !important;
}

.v-date-picker-header__value button {
  font-family: 'Lato', sans-serif !important;
  font-weight: 700 !important;
  font-size: 0.95rem !important;
  color: #1e293b !important;
}

.v-date-picker-header .v-btn {
  color: #64748b !important;
}

.v-date-picker-table thead th {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.72rem !important;
  font-weight: 600 !important;
  color: #94a3b8 !important;
  padding: 8px 0 !important;
}

.v-date-picker-table .v-btn {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.82rem !important;
  font-weight: 500 !important;
  color: #334155 !important;
}

.v-date-picker-table .v-btn:hover {
  background: #f0f9ff !important;
}

.v-date-picker-table .v-btn--active {
  background: #0ea5e9 !important;
  color: #fff !important;
  font-weight: 700 !important;
}

.v-date-picker-table .v-btn--active:hover {
  background: #0284c7 !important;
}

.v-date-picker-table .v-btn--outlined {
  border-color: #0ea5e9 !important;
  color: #0ea5e9 !important;
}

.v-picker__actions {
  padding: 8px 16px 14px !important;
  background: #fff !important;
  border-top: 1px solid #f1f5f9 !important;
}

.v-picker__actions .v-btn {
  font-family: 'Lato', sans-serif !important;
  font-weight: 600 !important;
  font-size: 0.82rem !important;
  text-transform: none !important;
  border-radius: 8px !important;
  padding: 0 16px !important;
}

/* Error state — red border on invalid inputs */
.ev-form-input.v-input--has-state.error--text fieldset,
.ev-form-input.v-input--has-state.error--text:hover fieldset {
  border-color: #ef4444 !important;
  border-width: 1px !important;
}

.ev-form-input.v-input--has-state.error--text .v-icon {
  color: #ef4444 !important;
}

.ev-form-input .v-messages__message {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  color: #ef4444 !important;
}
/* Modal overlay */
.ev-modal-overlay {
  box-shadow: none !important;
  border-radius: 20px !important;
  overflow: hidden;
}

/* Dark mode table */
.dark-mode .ev-data-table .v-data-table-header { background: var(--app-page); }
.dark-mode .ev-data-table .v-data-table-header th { color: var(--app-text-muted) !important; border-color: var(--app-border) !important; }
.dark-mode .ev-data-table tbody td { color: var(--app-text) !important; border-color: var(--app-border) !important; }
.dark-mode .ev-data-table tbody tr:hover { background: var(--app-border) !important; }
.dark-mode .ev-data-table .v-data-footer { background: var(--app-page); border-color: var(--app-border) !important; }

/* >>> dark companions: the light rules above pin these to a dark ink
   with !important, which on the dark field renders the text invisible
   rather than absent. Token references only. <<< */

.dark-mode .ev-form-input input,
.dark-mode .ev-form-input textarea,
.dark-mode .ev-form-input .v-select__selection {
  color: var(--app-text) !important;
}
</style>
